import { type Option, Select } from "@backstage/ui";

import type { CodeScheme } from "../../types";
import styles from "./CodeThemePicker.module.css";

/**
 * Props for the {@link CodeThemePicker} component.
 *
 * @public
 */
interface CodeThemePickerProps {
    /** The list of available color schemes to display as options. */
    schemes: CodeScheme[];
    /** The name of the currently selected scheme. */
    value: string;
    /** Callback invoked with the selected scheme name when the user picks a new option. */
    onChange: (name: string) => void;
}

/**
 * A labeled select control that lets users choose a syntax-highlight color scheme.
 *
 * Renders a {@link https://ui.backstage.io | @backstage/ui} `Select` whose options
 * are derived from the provided `schemes` array. The `onChange` callback receives
 * the scheme name as a plain string.
 *
 * @param props - See {@link CodeThemePickerProps}.
 * @public
 */
export const CodeThemePicker = ({
    schemes,
    value,
    onChange,
}: CodeThemePickerProps) => {
    const options: Option[] = schemes.map(s => ({
        value: s.name,
        label: s.name,
    }));

    return (
        <div className={styles.row}>
            <Select
                label="Theme"
                options={options}
                value={value}
                onChange={key => onChange(String(key))}
            />
        </div>
    );
};
